import { ChevronDown, IndianRupee, Plus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { fetchWrapper } from "src/helpers";
import { formatCurrencyWithLocalString } from "src/helpers/numberFormatter";
import { alertService } from "src/services";
import { PortfolioFolder } from "./PortfolioFolder";

export type PortfolioOption = {
  value: number | string;
  label: string;
  investment?: number;
  currentBalance?: number;
  pnl_percent?: number;
  pnl?: number;
  isPositive?: boolean;
  stock_investment?: number;
  risk_hits?: number;
  reward_hits?: number;
};

interface AddFundModalProps {
  isOpen: boolean;
  onClose: () => void;
  portfolios: PortfolioOption[];
  initialPortfolioId?: number | string | null;
  token?: string;
  onSuccess: (folder: PortfolioFolder) => void;
  initialAmount?: number;
  isTopUp?: boolean;
}

const AddFundModal = ({
  isOpen,
  onClose,
  portfolios,
  initialPortfolioId,
  token,
  onSuccess,
  initialAmount,
  isTopUp,
}: AddFundModalProps) => {
  const [amount, setAmount] = useState(
    initialAmount ? initialAmount.toString() : "",
  );
  const [amountError, setAmountError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [internalSelectedId, setInternalSelectedId] = useState<
    number | string | null
  >(null);

  useEffect(() => {
    if (isOpen) {
      setInternalSelectedId((prev) => {
        if (prev !== null) return prev;
        if (initialPortfolioId) return initialPortfolioId;
        if (portfolios.length > 0) return portfolios[0].value;
        return null;
      });
    } else {
      setInternalSelectedId(null);
    }
  }, [isOpen, initialPortfolioId, portfolios]);

  useEffect(() => {
    if (isOpen && initialAmount) {
      setAmount(initialAmount.toString());
    }
  }, [isOpen, initialAmount]);

  const selectedPortfolio = useMemo(
    () => portfolios.find((p) => p.value === internalSelectedId),
    [portfolios, internalSelectedId],
  );

  const handleAddFund = async () => {
    if (!token) {
      alertService.error("User token missing");
      return;
    }

    if (!internalSelectedId) {
      alertService.error("Select a portfolio");
      return;
    }

    if (!amount) {
      setAmountError("Amount is required");
      return;
    }

    const numericAmount = Number(amount);
    const portfolioIdNumber = Number(internalSelectedId);

    if (Number.isNaN(portfolioIdNumber)) {
      alertService.error("Invalid portfolio selection");
      return;
    }

    if (Number.isNaN(numericAmount) || numericAmount <= 0) {
      setAmountError("Enter an amount greater than zero");
      return;
    }

    let body: Record<string, number | string>;
    body = {
      portfolio_id: portfolioIdNumber,
      amount: numericAmount,
      action: "add",
    };
    try {
      setSubmitting(true);
      const response = await fetchWrapper.post(
        "/api/users/portfolio/funds",

        body,

        {
          Authorization: `Bearer ${token}`,
        },
      );

      if (!response.success) {
        alertService.error(response.message || "Failed to add funds");
        return;
      }
      alertService.success("Funds added successfully");
      onSuccess({
        id: response.portfolio.id,
        name: response.portfolio.name,
        investment: Number(response.portfolio.investment ?? 0),
      });
      setAmount("");
      setAmountError("");
      onClose();
    } catch (error) {
      alertService.error("Failed to add funds");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm p-4 h-full">
      <div className="bg-white dark:bg-[#1E1E2D] rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Add Funds to Portfolio
            </h3>
            <button
              onClick={() => {
                onClose();
                setAmount("");
                setAmountError("");
                setShowDropdown(false);
              }}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors cursor-pointer"
            >
              <X
                size={24}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              />
            </button>
          </div>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {isTopUp ? "Portfolio" : "Select Portfolio"}
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowDropdown((prev) => !prev)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#2A2B3D] text-gray-900 dark:text-white py-2.5 pl-3 pr-3 text-left focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm flex items-center justify-between cursor-pointer"
                >
                  <span className="truncate">
                    {selectedPortfolio?.label || "Select portfolio..."}
                  </span>
                  {!isTopUp && (
                    <ChevronDown
                      className={`h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform ${showDropdown ? "transform rotate-180" : ""}`}
                    />
                  )}
                </button>
                {!isTopUp && showDropdown && (
                  <div className="absolute z-10 mt-1 w-full rounded-md bg-white dark:bg-[#2A2B3D] shadow-lg border border-gray-200 dark:border-gray-600 max-h-48 overflow-auto">
                    <div className="p-2">
                      {portfolios.map((portfolio) => (
                        <button
                          key={portfolio.value}
                          className="w-full text-left p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                          onClick={() => {
                            setInternalSelectedId(portfolio.value);
                            setShowDropdown(false);
                          }}
                        >
                          {portfolio.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Amount to Add
              </label>
              <div className="relative rounded-lg shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <IndianRupee className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={amount}
                  onChange={(e) => {
                    let val = e.target.value;
                    val = val.replace(/\D/g, "");
                    if (val.length > 12) {
                      val = val.slice(0, 12);
                    }
                    if (val.length > 1 && val.startsWith("0")) {
                      val = val.replace(/^0+/, "");
                    }

                    setAmount(val);
                    if (val && Number(val) > 0) {
                      setAmountError("");
                    }
                  }}
                  className={`focus:ring-2 focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-12 py-3 sm:text-sm border ${
                    amountError
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 dark:border-gray-600"
                  } dark:bg-[#2A2B3D] dark:text-white rounded-lg transition-all cursor-text`}
                  placeholder="0"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                    INR
                  </span>
                </div>
              </div>
              {amountError && (
                <p className="mt-1 text-xs text-red-500">{amountError}</p>
              )}
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Current balance:{" "}
                {formatCurrencyWithLocalString(
                  Number(selectedPortfolio?.investment) +
                    Number(selectedPortfolio?.currentBalance),
                )}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-4">
              <button
                onClick={() => {
                  onClose();
                  setAmount("");
                  setAmountError("");
                  setShowDropdown(false);
                }}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleAddFund}
                disabled={submitting || amountError !== "" || !amount}
                className="px-4 py-2.5 text-sm font-medium text-white bg-[#00A85F] hover:bg-[#009952] rounded-lg focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <Plus size={16} />
                {submitting ? "Processing..." : "Add Funds"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFundModal;
